import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Radio,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Typography,
  Divider,
} from '@mui/material';
import { Translate } from 'react-localize-redux';

// Local, dependency-light money formatter. We deliberately avoid importing the
// `../../../utils` barrel here: it pulls in `jose` (ESM), which the jest transform
// can't handle, breaking this component's unit tests.
const formatAmount = (amount: number, currency: string, languageCode: string): string => {
  try {
    return `${amount.toLocaleString(languageCode)} ${currency}`;
  } catch {
    return `${amount} ${currency}`;
  }
};

export interface MovableBillItem {
  id: number;
  concept: string;
  amount: number;
}

export interface DraftBillOption {
  uuid: string;
  documentNumber: number;
}

interface SplitBillModalProps {
  open: boolean;
  onClose: () => void;
  /** The source DRAFT bill's items (only persisted items with an id are movable). */
  items: MovableBillItem[];
  /** Other DRAFT bills of the same patient (targets for "move to existing"). */
  otherDraftBills: DraftBillOption[];
  currency: string;
  languageCode: string;
  onConfirm: (payload: {
    billItemIds: number[];
    target: { type: 'new' } | { type: 'existing'; uuid: string };
  }) => Promise<void> | void;
  loading?: boolean;
  /** Pre-selected item ids — for Storybook states and tests; defaults to none. */
  initialSelectedIds?: number[];
}

/**
 * Move selected bill items into a new or existing DRAFT invoice of the same patient.
 *
 * Self-contained: it owns item selection (a checklist) AND target choice, so the host
 * screen only needs to render it — no changes to the bill-items table. Presentational:
 * the parent's `onConfirm` performs the API call + refresh.
 */
const SplitBillModal: React.FC<SplitBillModalProps> = ({
  open,
  onClose,
  items,
  otherDraftBills,
  currency,
  languageCode,
  onConfirm,
  loading = false,
  initialSelectedIds = [],
}) => {
  const hasExistingDrafts = otherDraftBills.length > 0;
  const [selectedIds, setSelectedIds] = useState<number[]>(initialSelectedIds);
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [targetUuid, setTargetUuid] = useState<string>('');
  const [error, setError] = useState<string>('');

  const sourceWillBeEmpty = items.length > 0 && selectedIds.length === items.length;

  const toggleItem = (id: number) => {
    setError('');
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleClose = () => {
    setSelectedIds([]);
    setMode('new');
    setTargetUuid('');
    setError('');
    onClose();
  };

  const handleConfirm = async () => {
    setError('');
    if (selectedIds.length === 0) {
      setError('hospital.billing.split_bill.no_items');
      return;
    }
    if (mode === 'existing' && !targetUuid) {
      setError('hospital.billing.split_bill.select_target');
      return;
    }
    try {
      await onConfirm({
        billItemIds: selectedIds,
        target: mode === 'new' ? { type: 'new' } : { type: 'existing', uuid: targetUuid },
      });
      handleClose();
    } catch (err) {
      setError('hospital.billing.split_bill.error');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Translate id="hospital.billing.split_bill.title" />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error"><Translate id={error} /></Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              <Translate id="hospital.billing.split_bill.description" />
            </Typography>
            <FormGroup>
              {items.map((item) => (
                <FormControlLabel
                  key={item.id}
                  control={
                    <Checkbox
                      checked={selectedIds.includes(item.id)}
                      onChange={() => toggleItem(item.id)}
                      disabled={loading}
                    />
                  }
                  label={`${item.concept} — ${formatAmount(item.amount, currency, languageCode)}`}
                />
              ))}
            </FormGroup>
          </Grid>

          {sourceWillBeEmpty && (
            <Grid item xs={12}>
              <Alert severity="warning">
                <Translate id="hospital.billing.split_bill.source_will_be_empty" />
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}><Divider /></Grid>

          <Grid item xs={12}>
            <FormControl component="fieldset" disabled={loading}>
              <RadioGroup
                value={mode}
                onChange={(e) => { setMode(e.target.value as 'new' | 'existing'); setError(''); }}
              >
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label={<Translate id="hospital.billing.split_bill.new_invoice" />}
                />
                <FormControlLabel
                  value="existing"
                  control={<Radio />}
                  disabled={!hasExistingDrafts}
                  label={<Translate id="hospital.billing.split_bill.existing_invoice" />}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {mode === 'existing' && hasExistingDrafts && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="split-target-label">
                  <Translate id="hospital.billing.split_bill.select_target" />
                </InputLabel>
                <Select
                  labelId="split-target-label"
                  value={targetUuid}
                  onChange={(e) => setTargetUuid(e.target.value as string)}
                  label={<Translate id="hospital.billing.split_bill.select_target" />}
                  disabled={loading}
                >
                  {otherDraftBills.map((bill) => (
                    <MenuItem key={bill.uuid} value={bill.uuid}>
                      #{bill.documentNumber}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          <Translate id="general.cancel" />
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading || selectedIds.length === 0 || (mode === 'existing' && !targetUuid)}
          startIcon={loading && <CircularProgress size={20} />}
        >
          <Translate id="hospital.billing.split_bill.move" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitBillModal;
